import React, { useEffect, useState } from 'react';

/*
import {
  AppInsightsContext,
  AppInsightsErrorBoundary,
} from '@microsoft/applicationinsights-react-js'
import { reactPlugin } from './AppInsights'
*/

import { IntlProvider } from 'react-intl';
import { IMeshNode } from '@csdental/dicom-reader';
import { Main } from './Main';
import { ViewOrientation, LangLocale, AppStatus } from './shared/common';
import { Mesh } from './model/Mesh';
import { ToolID } from './shared/common';
import { MarginLineModel } from './model/MarginLineModel';
import vtkActor from 'vtk.js/Sources/Rendering/Core/Actor';
import vtkPicker from 'vtk.js/Sources/Rendering/Core/Picker';

import occlusionMapping from './model/OcclusionMapping';

interface PatientInfo {
  firstName: string;
  lastName: string;
  id: string;
}

interface AcquisitionInfo {
  acquisitionTime: string;
  manufactModeName: string;
  deviceSerialNum: string;
  acquisitionType: string;
  acquisitionSoftwareVersion: string;
}

interface BusyDlgProp {
  visible: boolean;
  title: string;
}

interface AppContextInterface {
  locale: string;
  setLocale: React.Dispatch<React.SetStateAction<string>>;

  meshData: IMeshNode[];
  setMeshData: React.Dispatch<React.SetStateAction<IMeshNode[]>>;

  normalMeshes: Mesh[];
  setNormalMeshes: React.Dispatch<React.SetStateAction<Mesh[]>>;

  buccalMeshes: Mesh[];
  setBuccalMeshes: React.Dispatch<React.SetStateAction<Mesh[]>>;

  patient: PatientInfo;
  setPatient: React.Dispatch<React.SetStateAction<PatientInfo>>;

  acqInfo: AcquisitionInfo;
  setAcqInfo: React.Dispatch<React.SetStateAction<AcquisitionInfo>>;

  light: boolean;
  setLight: React.Dispatch<React.SetStateAction<boolean>>;

  color: boolean;
  setColor: React.Dispatch<React.SetStateAction<boolean>>;

  zoomFit: boolean;
  setZoomFit: React.Dispatch<React.SetStateAction<boolean>>;

  resetCamera: boolean;
  setResetCamera: React.Dispatch<React.SetStateAction<boolean>>;

  refreshCanvas: boolean;
  setRefreshCanvas: React.Dispatch<React.SetStateAction<boolean>>;

  viewOrientation: ViewOrientation;
  setViewOrientation: React.Dispatch<React.SetStateAction<ViewOrientation>>;

  snapshot: boolean;
  setSnapshot: React.Dispatch<React.SetStateAction<boolean>>;

  snapshotImage: string;
  setSnapshotImage: React.Dispatch<React.SetStateAction<string>>;

  galleryChanged: boolean;
  setGalleryChanged: React.Dispatch<React.SetStateAction<boolean>>;

  textureMesh: boolean;
  setTextureMesh: React.Dispatch<React.SetStateAction<boolean>>;

  currentTool: ToolID;
  setCurrentTool: React.Dispatch<React.SetStateAction<ToolID>>;

  dualView: boolean;
  setDualView: React.Dispatch<React.SetStateAction<boolean>>;

  withMarginLine: boolean;
  setWithMarginLine: React.Dispatch<React.SetStateAction<boolean>>;

  withBuccalBite: boolean;
  setWithBuccalBite: React.Dispatch<React.SetStateAction<boolean>>;

  occlusalPlaneActor: vtkActor;
  setOcclusalPlaneActor: React.Dispatch<React.SetStateAction<vtkActor>>;

  meshPicker: vtkPicker;

  selectedMesh: Mesh;
  setSelectedMesh: React.Dispatch<React.SetStateAction<Mesh>>;

  selectedActor: vtkActor;
  setSelectedActor: React.Dispatch<React.SetStateAction<vtkActor>>;

  rotationStep: number;
  setRotationStep: React.Dispatch<React.SetStateAction<number>>;

  movementStep: number;
  setMovementStep: React.Dispatch<React.SetStateAction<number>>;

  rotationX: number;
  setRotationX: React.Dispatch<React.SetStateAction<number>>;

  rotationY: number;
  setRotationY: React.Dispatch<React.SetStateAction<number>>;

  rotationZ: number;
  setRotationZ: React.Dispatch<React.SetStateAction<number>>;

  movementLowerZ: number;
  setMovementLowerZ: React.Dispatch<React.SetStateAction<number>>;

  movementUpperZ: number;
  setMovementUpperZ: React.Dispatch<React.SetStateAction<number>>;

  occlusalPlaneVisible: boolean;
  setOcclusalPlaneVisible: React.Dispatch<React.SetStateAction<boolean>>;

  busyDlgProp: BusyDlgProp;
  setBusyDlgProp: React.Dispatch<React.SetStateAction<BusyDlgProp>>;

  marginLines: Array<MarginLineModel>;
  setMarginLines: React.Dispatch<React.SetStateAction<Array<MarginLineModel>>>;

  marginLineVisible: boolean;
  setMarginLineVisible: React.Dispatch<React.SetStateAction<boolean>>;

  occluAvailable: boolean;
  setOccluAvailable: React.Dispatch<React.SetStateAction<boolean>>;

  status: AppStatus;
  setStatus: React.Dispatch<React.SetStateAction<AppStatus>>;

  exportAvailable: boolean;
  setExportAvailable: React.Dispatch<React.SetStateAction<boolean>>;
}

const AppContext = React.createContext<AppContextInterface>(null);

function loadLocaleData(locale: string) {
  switch (locale) {
    case LangLocale.fr:
    case LangLocale.fr_FR:
      return require('../compiled-locales/fr_FR.json');
    case LangLocale.zh_CN:
      return require('../compiled-locales/zh_CN.json');
    case LangLocale.zh_TW:
      return require('../compiled-locales/zh_TW.json');
    case LangLocale.ja:
      return require('../compiled-locales/ja_JP.json');
    case LangLocale.tr:
      return require('../compiled-locales/tr_TR.json');
    case LangLocale.sv:
      return require('../compiled-locales/sv_SE.json');
    case LangLocale.ru:
      return require('../compiled-locales/ru_RU.json');
    case LangLocale.pt:
      return require('../compiled-locales/pt_PT.json');
    case LangLocale.nl:
      return require('../compiled-locales/nl_NL.json');
    case LangLocale.it:
      return require('../compiled-locales/it_IT.json');
    case LangLocale.es:
      return require('../compiled-locales/es_ES.json');
    case LangLocale.de:
      return require('../compiled-locales/de_DE.json');
    case LangLocale.da:
      return require('../compiled-locales/da_DK.json');
    case LangLocale.en:
    case LangLocale.en_US:
    default:
      return require('../compiled-locales/en_US.json');
  }
}

const App = () => {
  const [locale, setLocale] = useState(navigator.language);
  const [messages, setMessages] = useState({});
  const [patient, setPatient] = useState<PatientInfo>({
    firstName: '',
    lastName: '',
    id: '',
  });
  const [acqInfo, setAcqInfo] = useState<AcquisitionInfo>({
    acquisitionTime: '',
    manufactModeName: '',
    deviceSerialNum: '',
    acquisitionType: '',
    acquisitionSoftwareVersion: '',
  });
  const [light, setLight] = useState(false);
  const [color, setColor] = useState(true);
  const [zoomFit, setZoomFit] = useState(false);
  const [resetCamera, setResetCamera] = useState(false);
  const [refreshCanvas, setRefreshCanvas] = useState(false);
  const [snapshot, setSnapshot] = useState(false);
  const [snapshotImage, setSnapshotImage] = useState('');
  const [galleryChanged, setGalleryChanged] = useState(false);
  const [textureMesh, setTextureMesh] = useState(false);

  const [meshData, setMeshData] = useState<IMeshNode[]>([]);
  const [normalMeshes, setNormalMeshes] = useState<Mesh[]>([]);
  const [buccalMeshes, setBuccalMeshes] = useState<Mesh[]>([]);

  const [viewOrientation, setViewOrientation] = useState(ViewOrientation.Unknown);

  const [occlusalPlaneActor, setOcclusalPlaneActor] = useState<vtkActor>(vtkActor.newInstance());
  const [occlusalPlaneVisible, setOcclusalPlaneVisible] = useState(true);
  const [meshPicker, setMeshPicker] = useState<vtkPicker>(vtkPicker.newInstance());
  const [selectedMesh, setSelectedMesh] = useState<Mesh>(null);
  const [selectedActor, setSelectedActor] = useState<vtkActor>(null);
  const [rotationStep, setRotationStep] = useState(0.5);
  const [movementStep, setMovementStep] = useState(0.01);
  const [rotationX, setRotationX] = useState(0.0);
  const [rotationY, setRotationY] = useState(0.0);
  const [rotationZ, setRotationZ] = useState(0.0);
  const [movementLowerZ, setMovementLowerZ] = useState(0.0);
  const [movementUpperZ, setMovementUpperZ] = useState(0.0);

  const [currentTool, setCurrentTool] = useState<ToolID | null>(null);

  const [dualView, setDualView] = useState(false);
  const [withMarginLine, setWithMarginLine] = useState(false);
  const [withBuccalBite, setWithBuccalBite] = useState(false);
  const [busyDlgProp, setBusyDlgProp] = useState<BusyDlgProp>({
    visible: false,
    title: '',
  });

  const [marginLines, setMarginLines] = useState<MarginLineModel[]>([]);
  const [marginLineVisible, setMarginLineVisible] = useState<boolean>(false);
  const [occluAvailable, setOccluAvailable] = useState(false);
  const [status, setStatus] = useState<AppStatus>(AppStatus.Idle);

  const [exportAvailable, setExportAvailable] = useState(false);

  useEffect(() => {
    setMessages(loadLocaleData(locale));
  }, [locale]);

  occlusionMapping.setOnWasmAvailable(() => {
    setOccluAvailable(true);
  });

  useEffect(() => {
    let selected_mesh = null;
    normalMeshes.forEach((mesh) => {
      if (mesh.actor === selectedActor) {
        selected_mesh = mesh;
      }
    });
    if (selectedMesh != selected_mesh) {
      if (selected_mesh != null && selected_mesh.isSelectable()) {
        console.log('set selected msh ', selected_mesh);
        setSelectedMesh(selected_mesh);
      }
    }
    setRefreshCanvas(true);
  }, [selectedActor]);

  useEffect(() => {
    if (acqInfo?.deviceSerialNum && acqInfo?.deviceSerialNum !== '') {
      const API_URL = `/ajax/user.php?action=getProfile&sn=${acqInfo.deviceSerialNum}`;
      fetch(API_URL)
        .then((response) => response.json())
        .then((profile) => setExportAvailable(profile['canExport']))
        .catch(function (err) {
          console.log('Fetch Error:', err);
        });
    } else {
      setExportAvailable(meshData.length > 1);
    }
  }, [acqInfo]);

  return (
    <AppContext.Provider
      value={{
        locale,
        setLocale,
        meshData,
        setMeshData,
        normalMeshes,
        setNormalMeshes,
        buccalMeshes,
        setBuccalMeshes,
        patient,
        setPatient,
        acqInfo,
        setAcqInfo,
        light,
        setLight,
        color,
        setColor,
        zoomFit,
        setZoomFit,
        resetCamera,
        setResetCamera,
        refreshCanvas,
        setRefreshCanvas,
        viewOrientation,
        setViewOrientation,
        snapshot,
        setSnapshot,
        snapshotImage,
        setSnapshotImage,
        galleryChanged,
        setGalleryChanged,
        textureMesh,
        setTextureMesh,
        currentTool,
        setCurrentTool,
        dualView,
        setDualView,
        withBuccalBite,
        setWithBuccalBite,
        withMarginLine,
        setWithMarginLine,
        marginLines,
        setMarginLines,
        marginLineVisible,
        setMarginLineVisible,
        busyDlgProp,
        setBusyDlgProp,
        occluAvailable,
        setOccluAvailable,
        occlusalPlaneActor,
        setOcclusalPlaneActor,
        meshPicker,
        selectedMesh,
        setSelectedMesh,
        selectedActor,
        setSelectedActor,
        rotationStep,
        setRotationStep,
        movementStep,
        setMovementStep,
        rotationX,
        setRotationX,
        rotationY,
        setRotationY,
        rotationZ,
        setRotationZ,
        movementLowerZ,
        setMovementLowerZ,
        movementUpperZ,
        setMovementUpperZ,
        occlusalPlaneVisible,
        setOcclusalPlaneVisible,
        status,
        setStatus,
        exportAvailable,
        setExportAvailable,
      }}
    >
      <IntlProvider locale={locale} defaultLocale="en-US" messages={messages}>
        <Main setLocale={setLocale}></Main>
      </IntlProvider>
    </AppContext.Provider>
  );
};

export { App, AppContext };
